import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Grid, Button } from '@mui/material';
import { motion } from 'framer-motion';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import DownloadIcon from '@mui/icons-material/Download';

function useTypewriter(words, speed = 68, deleteSpeed = 36, pause = 2600) {
  const [displayed, setDisplayed] = useState('');
  const [idx,       setIdx]       = useState(0);
  const [deleting,  setDeleting]  = useState(false);

  useEffect(() => {
    const word = words[idx];
    let t;
    if (!deleting && displayed === word) {
      t = setTimeout(() => setDeleting(true), pause);
    } else if (deleting && displayed === '') {
      setDeleting(false);
      setIdx((i) => (i + 1) % words.length);
    } else if (deleting) {
      t = setTimeout(() => setDisplayed(word.slice(0, displayed.length - 1)), deleteSpeed);
    } else {
      t = setTimeout(() => setDisplayed(word.slice(0, displayed.length + 1)), speed);
    }
    return () => clearTimeout(t);
  }, [displayed, deleting, idx, words, speed, deleteSpeed, pause]);

  return displayed;
}

const ROLES = ['Software Developer', 'Full-Stack Engineer', 'Systems Designer', 'Problem Solver'];

const stagger = {
  hidden:  { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.14 } },
};
const up = {
  hidden:  { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.72, ease: [0.22, 1, 0.36, 1] } },
};

export default function Home() {
  const typedRole = useTypewriter(ROLES);

  return (
    <>
      <style>{`
        @keyframes cursorBlink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0; }
        }
        @keyframes ringBreath {
          0%, 100% { opacity: 0.95; }
          50%       { opacity: 1; }
        }
      `}</style>

      <Container
        maxWidth="lg"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          py: { xs: 8, md: 0 },
        }}
      >
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">

          {/* ── Left: Text ───────────────────────────────────── */}
          <Grid item xs={12} md={8} sx={{ order: { xs: 2, md: 1 } }}>
            <motion.div variants={stagger} initial="hidden" animate="visible">

              {/* Eyebrow */}
              <motion.div variants={up}>
                <Typography sx={{
                  fontSize: '0.76rem',
                  fontWeight: 500,
                  color: '#2997FF',
                  textTransform: 'uppercase',
                  letterSpacing: '0.18em',
                  mb: 2,
                }}>
                  Hello, I'm
                </Typography>
              </motion.div>

              {/* Name */}
              <motion.div variants={up}>
                <Typography variant="h1" sx={{ mb: 1.5 }}>
                  Michael<br />Yarmoshik
                </Typography>
              </motion.div>

              {/* Typewriter */}
              <motion.div variants={up}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 5,
                  height: 'clamp(28px, 3.4vw, 36px)',
                }}>
                  <Typography component="span" sx={{
                    fontSize: 'clamp(18px, 2.5vw, 24px)',
                    fontWeight: 400,
                    color: 'rgba(245,245,247,0.55)',
                    letterSpacing: '-0.02em',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}>
                    {typedRole}
                  </Typography>
                  <Box sx={{
                    width: 2,
                    height: 'clamp(18px, 2.5vw, 24px)',
                    background: '#2997FF',
                    ml: 0.6,
                    borderRadius: '1px',
                    flexShrink: 0,
                    animation: 'cursorBlink 1s step-end infinite',
                  }} />
                </Box>
              </motion.div>

              {/* Bio */}
              <motion.div variants={up}>
                <Typography sx={{
                  fontSize: '17px',
                  lineHeight: 1.85,
                  color: 'rgba(245,245,247,0.55)',
                  maxWidth: '480px',
                  mb: 5,
                  letterSpacing: '-0.005em',
                }}>
                  Computer Science graduate (UW) and Founding Engineer at Uplane.
                  I ship full-stack products, from AI-driven ad systems to manufacturing automation and everything in between, with clean, intentional code.
                </Typography>
              </motion.div>

              {/* CTAs */}
              <motion.div variants={up}>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 7, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon sx={{ fontSize: '1rem' }} />}
                    href="/resume/Michael_Yarmoshik_Resume.pdf"
                    target="_blank"
                  >
                    Resume
                  </Button>
                  <Button variant="outlined" href="/contact">
                    Get in touch
                  </Button>
                </Box>
              </motion.div>

              {/* Social - icon only, no borders */}
              <motion.div variants={up}>
                <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center' }}>
                  {[
                    { Icon: GitHubIcon,   href: 'https://github.com/Kihsomray',  label: 'GitHub'   },
                    { Icon: LinkedInIcon, href: 'https://linkedin.com/in/myarmo', label: 'LinkedIn' },
                    { Icon: EmailIcon,    href: 'mailto:mike@zerotoil.com',        label: 'Email'    },
                  ].map(({ Icon, href, label }) => (
                    <Box
                      key={label}
                      component="a"
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      sx={{
                        color: 'rgba(245,245,247,0.35)',
                        textDecoration: 'none',
                        display: 'flex',
                        transition: 'color 0.18s ease',
                        '&:hover': { color: '#F5F5F7' },
                      }}
                    >
                      <Icon sx={{ fontSize: '1.35rem' }} />
                    </Box>
                  ))}
                </Box>
              </motion.div>

            </motion.div>
          </Grid>

          {/* ── Right: Photo ─────────────────────────────────── */}
          <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <Box sx={{
                position: 'relative',
                height: { xs: '32vh', sm: '44vh', md: '65vh', lg: '75vh' },
                maxWidth: { xs: 420, sm: 520, md: '100%' },
                mx: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {/* Ring */}
                <Box aria-hidden sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: { xs: 'translate(-50%, -50%)', md: 'translate(-50%, -56%)' },
                  width: { xs: '22vh', sm: '40%', md: '108%' },
                  maxWidth: { xs: 200, sm: 320, md: 780 },
                  aspectRatio: '1 / 1',
                  borderRadius: '50%',
                  border: {
                    xs: '14px solid rgba(255,255,255,0.98)',
                    sm: '10px solid rgba(255,255,255,0.98)',
                    md: '22px solid rgba(255,255,255,0.98)',
                  },
                  boxShadow: {
                    xs: 'inset 0 0 8px 0 rgba(255,255,255,0.1)',
                    md: 'inset 0 0 16px 0 rgba(255,255,255,0.1)',
                  },
                  animation: 'ringBreath 5s ease-in-out infinite',
                  zIndex: 0,
                }} />

                {/* Photo */}
                <Box
                  component="img"
                  src="/images/profile_picture_2.png"
                  alt="Michael Yarmoshik"
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: { xs: 'center', md: 'center bottom' },
                    WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 88%, transparent 100%)',
                    maskImage: 'linear-gradient(to bottom, black 0%, black 88%, transparent 100%)',
                    zIndex: 1,
                  }}
                />
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
